import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { fetchMyWallet, fetchMyWalletLedger, savePayoutAccount } from "../utils/walletEngine";

function WorkerWallet() {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    account_holder_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    upi_id: "",
    preferred_payout_mode: "upi",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setLoading(false);
        return;
      }

      setUser(user);

      const walletData = await fetchMyWallet(user.id);
      const ledgerData = await fetchMyWalletLedger(user.id);

      setWallet(walletData);
      setLedger(ledgerData);
    } catch (error) {
      console.error("Worker wallet load error:", error);
      alert(error.message || "Failed to load wallet.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayout = async () => {
    try {
      if (!user) return;

      await savePayoutAccount({
        owner_user_id: user.id,
        owner_role: "worker",
        account_holder_name: form.account_holder_name,
        bank_name: form.bank_name,
        account_number: form.account_number,
        ifsc_code: form.ifsc_code,
        upi_id: form.upi_id,
        preferred_payout_mode: form.preferred_payout_mode,
      });

      alert("Payout account saved");
    } catch (error) {
      console.error("Save payout error:", error);
      alert(error.message || "Failed to save payout account.");
    }
  };

  if (loading) {
    return <div style={page}><p>Loading wallet...</p></div>;
  }

  return (
    <div style={page}>
      <h1 style={title}>Worker Wallet</h1>
      <p style={sub}>Weekly settlement cycle. Payouts are processed on Saturday.</p>

      <div style={statsGrid}>
        <div style={card}>
          <p style={label}>Available Balance</p>
          <h2 style={value}>₹{Number(wallet?.available_balance || 0).toFixed(2)}</h2>
        </div>

        <div style={card}>
          <p style={label}>Pending Balance</p>
          <h2 style={value}>₹{Number(wallet?.pending_balance || 0).toFixed(2)}</h2>
        </div>

        <div style={card}>
          <p style={label}>Total Earned</p>
          <h2 style={value}>₹{Number(wallet?.total_earned || 0).toFixed(2)}</h2>
        </div>

        <div style={card}>
          <p style={label}>Total Deducted</p>
          <h2 style={value}>₹{Number(wallet?.total_deducted || 0).toFixed(2)}</h2>
        </div>
      </div>

      {Number(wallet?.available_balance || 0) < 0 && (
        <div style={dangerBox}>
          Your wallet is negative. Future earnings may first recover platform dues before payout.
        </div>
      )}

      <div style={sectionCard}>
        <h2>Payout Account</h2>

        <div style={formGrid}>
          <input
            style={input}
            placeholder="Account Holder Name"
            value={form.account_holder_name}
            onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
          />

          <input
            style={input}
            placeholder="Bank Name"
            value={form.bank_name}
            onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
          />

          <input
            style={input}
            placeholder="Account Number"
            value={form.account_number}
            onChange={(e) => setForm({ ...form, account_number: e.target.value })}
          />

          <input
            style={input}
            placeholder="IFSC Code"
            value={form.ifsc_code}
            onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })}
          />

          <input
            style={input}
            placeholder="UPI ID"
            value={form.upi_id}
            onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
          />

          <select
            style={input}
            value={form.preferred_payout_mode}
            onChange={(e) => setForm({ ...form, preferred_payout_mode: e.target.value })}
          >
            <option value="upi">UPI</option>
            <option value="bank_transfer">Bank Transfer</option>
          </select>
        </div>

        <button style={btn} onClick={handleSavePayout}>
          Save Payout Details
        </button>
      </div>

      <div style={sectionCard}>
        <h2>Recent Wallet Activity</h2>

        {ledger.length === 0 ? (
          <p style={sub}>No wallet activity yet.</p>
        ) : (
          <div style={{ display: "grid", gap: "12px" }}>
            {ledger.map((entry) => (
              <div key={entry.id} style={ledgerCard}>
                <div>
                  <strong>{formatEntryType(entry.entry_type)}</strong>
                  <p style={small}>{entry.description || "Wallet entry"}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <strong>{Number(entry.amount || 0) >= 0 ? "+" : ""}₹{Number(entry.amount || 0).toFixed(2)}</strong>
                  <p style={small}>{new Date(entry.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatEntryType(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const page = {
  minHeight: "100vh",
  background: "#050505",
  color: "#fff",
  padding: "32px",
};

const title = {
  marginBottom: "8px",
};

const sub = {
  color: "#b3b3b3",
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
  marginTop: "24px",
};

const card = {
  background: "linear-gradient(180deg, #111, #0a0a0a)",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "20px",
};

const label = {
  color: "#aaa",
  marginBottom: "8px",
};

const value = {
  margin: 0,
};

const sectionCard = {
  marginTop: "24px",
  background: "linear-gradient(180deg, #111, #0a0a0a)",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "22px",
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
  marginTop: "16px",
};

const input = {
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0c0c0c",
  color: "#fff",
};

const btn = {
  marginTop: "16px",
  padding: "12px 18px",
  borderRadius: "10px",
  border: "none",
  background: "#fff",
  color: "#000",
  cursor: "pointer",
  fontWeight: "bold",
};

const ledgerCard = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "14px",
  background: "#0c0c0c",
  borderRadius: "12px",
  border: "1px solid #1f1f1f",
};

const small = {
  fontSize: "12px",
  color: "#999",
  marginTop: "6px",
  marginBottom: 0,
};

const dangerBox = {
  marginTop: "20px",
  background: "rgba(255, 77, 77, 0.12)",
  color: "#ff8c8c",
  border: "1px solid rgba(255, 77, 77, 0.35)",
  padding: "14px",
  borderRadius: "12px",
};

export default WorkerWallet;